import "./index.scss";
import {
  AlgoliaHits,
  AlgoliaPagination,
  AlgoliaSearch,
  AlgoliaStateResults,
  useAlgoliaSubmit,
} from "@wise-emotions/react-algolia";
import { ButtonIcon } from "@wise-emotions/react-button";
import { CfImageProps } from "components/Image/types";
import { CfTranslations, GetTranslationsObj } from "types/types";
import { GeneralMediumSearch } from "@wise-emotions/react-icons";
import { Hit, StateResultsProvided } from "react-instantsearch-core";
import { getTranslations } from "helpers/contenful";
import { parse } from "qs";
import { toQueryString } from "@wise-emotions/web-utils";
import { useMinCharsSearch } from "context/SiteProvider";
import CfImage from "components/Image/CfImage";
import ItemResult from "components/Search/ItemResult";
import Message from "components/Search/Message";
import React, { ChangeEvent, useMemo, useState } from "react";
import Strip from "components/Strip";
import parser from "html-react-parser";

const REPLACE = "replace";
const PUSH = "push";

interface Params {
  s?: string;
  p?: string | number;
}

const setParamsPagination = (params: Params, reset = false) => {
  if (reset) {
    // @ts-expect-error
    return toQueryString(params);
  }
  const currentParams = parse(window.location.search, {
    ignoreQueryPrefix: true,
  });
  return toQueryString({ ...currentParams, ...params });
};

const updateUrlState = (url: string | undefined, type = PUSH) => {
  if (type === PUSH) {
    window.history.pushState({}, "", url);
  } else if (type === REPLACE) {
    window.history.replaceState({}, "", url);
  }
};

type Evt =
  | React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLElement>
  | React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement | HTMLElement>;

const onClickPagination = (page: number, evt: Evt) => {
  // @ts-expect-error currentTarget
  updateUrlState(evt.currentTarget.href);

  window.scrollTo({ top: 0, behavior: "smooth" });
};

const createUrlPagination = (item: string | number, currentPage: number) => {
  const currentPath = window.location.pathname;

  if (item === "LEFT") {
    return `${currentPath}?${setParamsPagination({ p: currentPage - 1 })}`;
  }
  if (item === "RIGHT") {
    return `${currentPath}?${setParamsPagination({ p: currentPage + 1 })}`;
  }
  return `${currentPath}?${setParamsPagination({ p: item })}`;
};

export interface FullSearchProps {
  title?: string;
  text?: string;
  image: CfImageProps;
  lang: string;
  translations: CfTranslations[];
  query?: string;
}

const SearchFull = ({
  title,
  text,
  image,
  lang,
  translations,
  query,
}: FullSearchProps) => {
  const memoizedTranslations = useMemo(() => {
    return getTranslations(translations) as GetTranslationsObj;
  }, [translations]);

  const minChars = useMinCharsSearch();

  const textSearch = text ? parser(text) : "";

  const [inputValue, setInputValue] = useState(query);

  const submit = useAlgoliaSubmit();

  const currentLangPath = `/${lang}/`;

  const updateQueryStringValue = (val: string | undefined) => {
    if (!val) return;
    if (val.length < minChars) {
      updateUrlState(`${window.location.pathname}`, REPLACE);
    } else {
      const str = setParamsPagination({ s: val }, true);
      updateUrlState(`${window.location.pathname}?${str}`, REPLACE);
    }
  };

  const onChange = (val: string) => {
    setInputValue(val);
    updateQueryStringValue(val);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (submit) {
      updateQueryStringValue(inputValue);
      submit();
    }
  };

  const onCancel = () => {
    setInputValue("");
    updateQueryStringValue("");
  };

  return (
    <div className="SearchFull">
      <Strip
        xsWrapContent="sm"
        backgroundColor="neutralQuaternary"
        hasPaddingWrapMobile
        customClass="SearchFull__section-top"
      >
        <form className="SearchForm__form" onSubmit={submitSearch}>
          <div className="SearchForm__input">
            <AlgoliaSearch
              enableSubmit
              searchProps={{
                label: memoizedTranslations["search-input-label"],
                placeholder: memoizedTranslations["search-input-placeholder"],
                name: "search",
                id: "search-page",
                cancelLabel: memoizedTranslations["search-input-cancel-label"],
                value: inputValue || "",
                onChange: (evt: ChangeEvent<HTMLInputElement>) =>
                  onChange(evt.currentTarget.value),
                onCancel,
              }}
            />
          </div>
          <div className="SearchForm__button" data-testid="search-button">
            <ButtonIcon
              ariaLabel="search"
              size="lg"
              type="submit"
              aria-label={memoizedTranslations["search-button-title"]}
            >
              <GeneralMediumSearch />
            </ButtonIcon>
          </div>
        </form>
      </Strip>

      <Strip
        xsWrapContent="sm"
        paddingBottom="lg"
        paddingTop="lg"
        hasPaddingWrapMobile
      >
        {inputValue && inputValue.length < minChars ? (
          <div className="SearchFull__empty">
            <h5 className="SearchFull__empty__title">{title}</h5>
            <div className="SearchFull__empty__text">
              <p>{textSearch}</p>
            </div>
            {image && (
              <div
                data-testid="empty-image"
                className="SearchFull__empty__image"
              >
                <CfImage {...image} />
              </div>
            )}
          </div>
        ) : (
          <div className="SearchFull__results">
            <AlgoliaStateResults>
              {({
                searching,
                searchState,
                searchResults,
                error,
              }: StateResultsProvided) => {
                return (
                  <>
                    {searching && (
                      <div className="SearchLoader">
                        <div className="SearchLoader__img" />
                      </div>
                    )}
                    {searchResults && searchResults.nbHits > 0 && (
                      <div className="SearchFull__results__recap">
                        <span>
                          {`${searchResults.nbHits} ${
                            memoizedTranslations["search-results-recap"]
                          } "${searchState.query || ""}"`}
                        </span>
                      </div>
                    )}
                    {searchResults && searchResults.nbHits === 0 && !error && (
                      <div
                        data-testid="no-result-image"
                        className="SearchFull__message"
                      >
                        <Message
                          abstract={
                            memoizedTranslations["search-not-found-text"]
                          }
                          title={memoizedTranslations["search-not-found-title"]}
                          iconName="general_medium_search"
                        />
                      </div>
                    )}
                    {error && (
                      <div className="SearchFull__message">
                        <Message
                          abstract={
                            memoizedTranslations["search-unavailable-text"]
                          }
                          title={
                            memoizedTranslations["search-unavailable-title"]
                          }
                          iconName="general_medium_sad"
                        />
                      </div>
                    )}
                  </>
                );
              }}
            </AlgoliaStateResults>
            <div className="SearchFull__list">
              <AlgoliaHits>
                {({ title: tit, abstract, __position: index, slug }: Hit) => {
                  const link = currentLangPath + slug;
                  return (
                    <ItemResult
                      link={link}
                      title={tit}
                      abstract={abstract}
                      index={index}
                    />
                  );
                }}
              </AlgoliaHits>
            </div>
            <div className="SearchFull__pagination">
              <AlgoliaPagination
                onClick={(currentPage, evt) =>
                  onClickPagination(currentPage, evt)
                }
                paginationProps={{
                  createUrl: (item, currentPage) =>
                    createUrlPagination(item, currentPage),
                  ariaNext: memoizedTranslations["search-pagination-aria-next"],
                  ariaPrevious:
                    memoizedTranslations["search-pagination-aria-prev"],
                  ariaPaginationPrefix:
                    memoizedTranslations["search-pagination-aria-page"],
                }}
              />
            </div>
          </div>
        )}
      </Strip>
    </div>
  );
};

export default SearchFull;
